/**
 * Pre-computed font metrics for every Google Font referenced in the
 * scene-template-seeder seeds, sourced from `@capsizecss/metrics`.
 * Capsize ships these as per-font modules so we only pay for what we
 * import (tree-shakes the rest).
 *
 * The map key is the exact `fontFamily` string used in seeds —
 * `OverlayTextRenderer` / `OverlayElementText` look up the metrics by
 * that key before passing to `getCapsizeTrim()`. Add a new entry here
 * any time the seed catalog adds a new fontFamily; missing entries
 * trigger a console warning and fall back to legacy centring.
 *
 * See `docs/dom-pixi-parity-plan.md` Sprint 1 for the full rationale.
 */

import type { FontMetrics } from "@capsizecss/core";

import anton from "@capsizecss/metrics/anton";
import audiowide from "@capsizecss/metrics/audiowide";
import bungee from "@capsizecss/metrics/bungee";
import cormorantGaramond from "@capsizecss/metrics/cormorantGaramond";
import dMMono from "@capsizecss/metrics/dMMono";
import inter from "@capsizecss/metrics/inter";
import italiana from "@capsizecss/metrics/italiana";
import monoton from "@capsizecss/metrics/monoton";
import orbitron from "@capsizecss/metrics/orbitron";
import pressStart2P from "@capsizecss/metrics/pressStart2P";
import roboto from "@capsizecss/metrics/roboto";
import spaceGrotesk from "@capsizecss/metrics/spaceGrotesk";
import vT323 from "@capsizecss/metrics/vT323";

export const FONT_METRICS: Record<string, FontMetrics> = {
  Anton: anton,
  Audiowide: audiowide,
  Bungee: bungee,
  "Cormorant Garamond": cormorantGaramond,
  "DM Mono": dMMono,
  Inter: inter,
  Italiana: italiana,
  Monoton: monoton,
  Orbitron: orbitron,
  // `@capsizecss/metrics/pressStart2P` ships `descent: 0` at runtime but its
  // bundled type declaration omits `descent` (dropped because it's 0), so the
  // module's inferred type doesn't satisfy `FontMetrics`. Re-add the field
  // (same value the runtime module already carries) to bridge the type gap.
  "Press Start 2P": { ...pressStart2P, descent: 0 },
  Roboto: roboto,
  "Space Grotesk": spaceGrotesk,
  VT323: vT323,
};

/**
 * Resolve metrics for a `font-family` CSS value (which may be a stack
 * like `"Inter, system-ui, sans-serif"`). Returns the first matching
 * entry, or `null` if no token in the stack has known metrics — the
 * caller should fall back to legacy centring in that case.
 */
export const resolveFontMetrics = (
  fontFamily: string | undefined,
): FontMetrics | null => {
  if (!fontFamily) return null;
  const tokens = fontFamily
    .split(",")
    .map((t) => t.trim().replace(/^["']|["']$/g, ""));
  for (const token of tokens) {
    const metrics = FONT_METRICS[token];
    if (metrics) return metrics;
  }
  return null;
};
